import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { adminApi, apiError } from "../adminApi";
import { Panel, Pill } from "../components/Bits";

const CATEGORIES = ["service", "broker", "pricing", "benefits", "tech", "stability", "texas"];

const ScoreCell = ({ partnerId, category, value, canEdit, onSave }) => {
  const [draft, setDraft] = useState(String(value));

  useEffect(() => { setDraft(String(value)); }, [value]);

  if (!canEdit) {
    return <span className="font-mono text-xs text-cream/60">{value}</span>;
  }
  return (
    <input
      type="number" min="1" max="10" value={draft}
      data-testid={`peo-score-${partnerId}-${category}`}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        const next = Number(draft);
        if (!next || next === value || next < 1 || next > 10) {
          setDraft(String(value));
          return;
        }
        onSave(category, next);
      }}
      className="w-12 rounded border border-cream/15 bg-cream/5 px-2 py-1 text-center font-mono text-xs text-cream outline-none transition-colors duration-200 focus:border-gold/60"
    />
  );
};

const Bar = ({ value, best }) => (
  <span className="inline-flex h-1.5 w-24 overflow-hidden rounded-full bg-cream/10 align-middle">
    <span className="h-full rounded-full bg-gold transition-all duration-500"
          style={{ width: `${best ? (value / best) * 100 : 0}%` }} />
  </span>
);

export default function AdminPeoProfiles() {
  const [data, setData] = useState(null);
  const [matrix, setMatrix] = useState(null);
  const [saving, setSaving] = useState("");

  const load = useCallback(async () => {
    const [partners, rules] = await Promise.allSettled([
      adminApi.get("/crm/peo/partners"),
      adminApi.get("/crm/peo/fit-matrix"),
    ]);
    if (partners.status === "fulfilled") setData(partners.value.data);
    if (rules.status === "fulfilled") setMatrix(rules.value.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (partnerId, category, value) => {
    setSaving(partnerId);
    try {
      const { data: fresh } = await adminApi.patch(`/crm/peo/partners/${partnerId}`,
                                                   { scores: { [category]: value } });
      toast.success(`${fresh.name} re-scored — now ${fresh.overall}/100`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving("");
    }
  };

  if (!data) {
    return (
      <div className="flex items-center gap-3 text-sm text-cream/40" data-testid="peo-profiles-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading the partner library…
      </div>
    );
  }

  const best = data.partners[0]?.overall || 100;

  return (
    <div className="space-y-7" data-testid="admin-peo-profiles-page">
      <div>
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-terra">PEO partners</p>
        <h1 className="mt-3 font-serif text-4xl font-light leading-none text-cream">
          Who to lead with, and why
        </h1>
        <p className="mt-3 max-w-3xl text-sm leading-relaxed text-cream/50">
          Wave 1, scored as of {data.as_of}. The top six sit within 4.5 points of each other, so fit
          beats rank — match the employer to a matrix row, then quote the first choice and the backup
          side by side. Every category is editable: re-score a partner the day a real quote lands and
          the weighted total follows.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        {[["Partners scored", data.partners.length, "peo-count-stat"],
          ["Leading", data.partners[0]?.name || "—", "peo-leader-stat"],
          ["Average score", `${data.average}/100`, "peo-average-stat"]].map(([label, value, testid]) => (
          <div key={label} className="rounded-xl border border-cream/10 bg-[#10211A] px-5 py-5"
               data-testid={testid}>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-cream/45">{label}</p>
            <p className="mt-3 font-serif text-2xl leading-none text-cream">{value}</p>
          </div>
        ))}
      </div>

      <Panel title="Scorecard" testid="panel-peo-scorecard">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[860px] border-collapse text-left">
            <thead>
              <tr className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/30">
                <th className="py-2 pr-4">Partner</th>
                <th className="py-2 pr-4">Score</th>
                {CATEGORIES.map((key) => (
                  <th key={key} className="py-2 pr-3">
                    {data.weights[key].label.split(" ")[0]}
                    <span className="block text-[9px] text-cream/20">
                      {Math.round(data.weights[key].weight * 100)}%
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-cream/10">
              {data.partners.map((partner) => (
                <tr key={partner.partner_id} data-testid={`peo-row-${partner.partner_id}`}>
                  <td className="py-3 pr-4">
                    <p className="text-sm text-cream/90">
                      {partner.name}
                      {saving === partner.partner_id ? (
                        <Loader2 className="ml-2 inline h-3 w-3 animate-spin text-gold" />
                      ) : null}
                    </p>
                    <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.18em] text-cream/30">
                      {partner.hq}
                    </p>
                    <p className="mt-1 max-w-sm text-xs leading-relaxed text-cream/45">
                      {partner.strengths}
                    </p>
                  </td>
                  <td className="py-3 pr-4 align-top">
                    <p className="font-serif text-xl leading-none text-gold"
                       data-testid={`peo-overall-${partner.partner_id}`}>
                      {partner.overall}
                    </p>
                    <Bar value={partner.overall} best={best} />
                  </td>
                  {CATEGORIES.map((key) => (
                    <td key={key} className="py-3 pr-3 align-top">
                      <ScoreCell partnerId={partner.partner_id} category={key}
                                 value={partner.scores[key]} canEdit
                                 onSave={(category, value) => save(partner.partner_id, category, value)} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-5 text-xs leading-relaxed text-cream/35" data-testid="peo-limits">
          {data.limits}
        </p>
      </Panel>

      <Panel title="Best-fit matrix" testid="panel-peo-matrix">
        <ul className="divide-y divide-cream/10">
          {(matrix?.rules || []).map((rule) => (
            <li key={rule.rule_id} className="py-4" data-testid={`peo-rule-${rule.rule_id}`}>
              <div className="flex flex-wrap items-baseline justify-between gap-3">
                <p className="text-sm text-cream/90">{rule.profile}</p>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/30">
                  {rule.size[0]}–{rule.size[1] > 5000 ? "1,000+" : rule.size[1]} employees
                </p>
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <Pill tone="gold">{rule.first_choice_name}</Pill>
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/25">
                  backup
                </span>
                <Pill tone="neutral">{rule.backup_name}</Pill>
              </div>
              <p className="mt-2 text-xs leading-relaxed text-cream/45">{rule.why}</p>
            </li>
          ))}
        </ul>
      </Panel>

      <div className="grid gap-5 lg:grid-cols-2">
        <Panel title="Renewal calendar" testid="panel-peo-renewals">
          <ul className="divide-y divide-cream/10">
            {data.partners.map((partner) => (
              <li key={partner.partner_id} className="py-3"
                  data-testid={`peo-renewal-${partner.partner_id}`}>
                <div className="flex flex-wrap items-baseline justify-between gap-3">
                  <p className="text-sm text-cream/85">{partner.name}</p>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold/70">
                    start {partner.start_review}
                  </p>
                </div>
                <p className="mt-1 text-xs leading-relaxed text-cream/45">
                  {partner.renewal_plan_year}
                  {partner.public_renewal_data ? ` · ${partner.public_renewal_data}` : ""}
                </p>
              </li>
            ))}
          </ul>
          <p className="mt-4 text-xs leading-relaxed text-cream/35" data-testid="peo-peak-season">
            {data.peak_season}
          </p>
        </Panel>

        <Panel title="Scoring methodology" testid="panel-peo-methodology">
          <ul className="divide-y divide-cream/10">
            {CATEGORIES.map((key) => (
              <li key={key} className="py-3" data-testid={`peo-weight-${key}`}>
                <div className="flex items-baseline justify-between gap-3">
                  <p className="text-sm text-cream/85">{data.weights[key].label}</p>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/40">
                    {Math.round(data.weights[key].weight * 100)}%
                  </p>
                </div>
                <p className="mt-1 text-xs leading-relaxed text-cream/45">
                  {data.weights[key].measures}
                </p>
              </li>
            ))}
          </ul>
        </Panel>
      </div>
    </div>
  );
}
